import { mergeClasses } from "@fluentui/react-components";
import { istObjekt, neueListenId, textOderNichts, useListenStyles } from "./listenStil";
import { asArray } from "./structuredAnswers";
import GrossZahlEingabe, { ziffernFilter } from "./GrossZahlEingabe";
import KategoriePicker from "./KategoriePicker";
import TypPicker from "./TypPicker";
import DachPicker from "./DachPicker";
import BauartklassePicker from "./BauartklassePicker";
import AusstattungsstufePicker from "./AusstattungsstufePicker";
import { DACH_TYPEN } from "./enums";

/**
 * Anbauten und weitere Gebäude auf dem Grundstück — mehrere Einträge, je mit
 * derselben Detailerfassung wie das Hauptgebäude (Gebäudeart, Dachform,
 * Bauartklasse und Bauausführung mit den bekannten Bildern; Baujahr,
 * Geschosse, Fläche, Einheiten).
 *
 * Fachabstimmung 31.08.2026: Ein Anbau kann eine andere Bauweise und ein
 * anderes Baujahr haben, das ändert die Wert-1914-Hochrechnung. Bis 10/2026
 * gab es nur je ein Freitextfeld für Art und Dach (`anbautenGebaeudeart`,
 * `anbautenDach`, `anbautenEinheiten`, `anbautenBauausfuehrung`);
 * `anbautenAusAntworten` macht daraus beim Öffnen den ersten Eintrag.
 *
 * Die Liste liegt unter **einem** Answer-Key `anbauten`.
 */

export const ANBAU_ARTEN = [
  "Anbau am Gebäude",
  "Freistehendes Nebengebäude",
  "Garage",
  "Carport",
  "Halle",
  "Scheune",
  "Wintergarten",
  "Sonstiges",
] as const;

export type Anbau = {
  id: string;
  art?: string;
  /** Freie Bezeichnung, z. B. „Garage Nordseite". */
  bezeichnung?: string;
  kategorie?: string;
  typ?: string;
  dachTyp?: string;
  dachAusgebaut?: string;
  bauartklasse?: string;
  bauausfuehrung?: string;
  baujahr?: string;
  geschosse?: string;
  flaeche?: string;
  einheiten?: string;
};

const FELDER = [
  "art",
  "bezeichnung",
  "kategorie",
  "typ",
  "dachTyp",
  "dachAusgebaut",
  "bauartklasse",
  "bauausfuehrung",
  "baujahr",
  "geschosse",
  "flaeche",
  "einheiten",
] as const;

export const ANBAU_ALT_SCHLUESSEL = [
  "anbautenGebaeudeart",
  "anbautenDach",
  "anbautenEinheiten",
  "anbautenBauausfuehrung",
] as const;

export const asAnbautenListe = (value: unknown): Anbau[] =>
  asArray<unknown>(value)
    .filter(istObjekt)
    .map((e, idx) => {
      const anbau: Anbau = { id: textOderNichts(e.id) ?? `anbau-${idx}` };
      for (const f of FELDER) {
        const v = textOderNichts(e[f]);
        if (v) anbau[f] = v;
      }
      return anbau;
    });

const DACH_LISTE: readonly string[] = DACH_TYPEN;

export const anbautenAusAntworten = (a: Record<string, unknown>): Anbau[] => {
  const liste = asAnbautenListe(a.anbauten);
  if (liste.length > 0) return liste;
  const art = textOderNichts(a.anbautenGebaeudeart);
  const dach = textOderNichts(a.anbautenDach);
  const einheiten = textOderNichts(a.anbautenEinheiten);
  const bauausfuehrung = textOderNichts(a.anbautenBauausfuehrung);
  if (!art && !dach && !einheiten && !bauausfuehrung) return [];
  const alt: Anbau = { id: "anbau-alt" };
  // Die frühere Angabe war Freitext — sie bleibt als Bezeichnung lesbar.
  const bezeichnung = [art, dach && !DACH_LISTE.includes(dach) ? `Dach: ${dach}` : null]
    .filter(Boolean)
    .join(", ");
  if (bezeichnung) alt.bezeichnung = bezeichnung;
  if (dach && DACH_LISTE.includes(dach)) alt.dachTyp = dach;
  if (einheiten) alt.einheiten = einheiten;
  if (bauausfuehrung) alt.bauausfuehrung = bauausfuehrung;
  return [alt];
};

const ANBAU_ART_LISTE: readonly string[] = ANBAU_ARTEN;

type Props = {
  entries: Anbau[];
  onChange: (next: Anbau[]) => void;
};

const AnbautenListe = ({ entries, onChange }: Props) => {
  const styles = useListenStyles();
  const update = (id: string, patch: Partial<Anbau>) =>
    onChange(entries.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  const remove = (id: string) => onChange(entries.filter((e) => e.id !== id));
  const add = () => onChange([...entries, { id: neueListenId("anbau") }]);

  return (
    <div className={styles.root}>
      {entries.length === 0 && <span className={styles.empty}>Noch kein Anbau erfasst.</span>}
      {entries.map((entry, idx) => {
        const nr = idx + 1;
        const titel = entry.bezeichnung || entry.art || `Anbau ${nr}`;
        return (
          <div key={entry.id} className={styles.card} data-testid={`anbau-${idx}`}>
            <div className={styles.head}>
              <span className={styles.badge}>{nr}</span>
              <span className={styles.title}>{titel}</span>
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => remove(entry.id)}
                aria-label={`Anbau ${nr} entfernen`}
              >
                ×
              </button>
            </div>
            <div className={styles.grid}>
              <div className={styles.field}>
                <span className={styles.label}>Art</span>
                <select
                  className={styles.input}
                  name={`anbauten-${idx}-art`}
                  aria-label={`Anbau ${nr} — Art`}
                  value={entry.art ?? ""}
                  onChange={(e) => update(entry.id, { art: e.target.value })}
                >
                  <option value="">Bitte wählen</option>
                  {ANBAU_ARTEN.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                  {entry.art && !ANBAU_ART_LISTE.includes(entry.art) && (
                    <option value={entry.art}>{entry.art}</option>
                  )}
                </select>
              </div>
              <div className={mergeClasses(styles.field, styles.fullSpan)}>
                <span className={styles.label}>Bezeichnung</span>
                <input
                  className={styles.input}
                  name={`anbauten-${idx}-bezeichnung`}
                  aria-label={`Anbau ${nr} — Bezeichnung`}
                  placeholder="z. B. Werkstatt hinter dem Haus"
                  value={entry.bezeichnung ?? ""}
                  onChange={(e) => update(entry.id, { bezeichnung: e.target.value })}
                />
              </div>
            </div>

            <span className={styles.label}>Gebäudeart</span>
            <KategoriePicker
              value={entry.kategorie ?? ""}
              onChange={(v) => update(entry.id, { kategorie: v, typ: "" })}
            />
            {entry.kategorie && (
              <TypPicker
                kategorie={entry.kategorie}
                value={entry.typ ?? ""}
                onChange={(v) => update(entry.id, { typ: v })}
              />
            )}

            <span className={styles.label}>Dachform</span>
            <DachPicker
              value={entry.dachTyp ?? ""}
              ausgebaut={entry.dachAusgebaut ?? ""}
              onChange={(v) => update(entry.id, { dachTyp: v })}
              onAusgebautChange={(v) => update(entry.id, { dachAusgebaut: v })}
            />

            <span className={styles.label}>Bauartklasse</span>
            <BauartklassePicker
              value={entry.bauartklasse ?? ""}
              onChange={(v) => update(entry.id, { bauartklasse: v })}
              ariaLabel={`Anbau ${nr} — Bauartklasse`}
            />

            <span className={styles.label}>Bauausführung</span>
            <AusstattungsstufePicker
              value={entry.bauausfuehrung ?? ""}
              onChange={(v) => update(entry.id, { bauausfuehrung: v })}
              ariaLabel={`Anbau ${nr} — Bauausführung`}
            />

            <div className={styles.grid}>
              <div className={styles.field}>
                <span className={styles.label}>Baujahr</span>
                <GrossZahlEingabe
                  value={entry.baujahr}
                  onChange={(v) => update(entry.id, { baujahr: v })}
                  ariaLabel={`Anbau ${nr} — Baujahr`}
                  platzhalter="JJJJ"
                  maxLen={4}
                />
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Geschosse</span>
                <input
                  className={styles.input}
                  name={`anbauten-${idx}-geschosse`}
                  aria-label={`Anbau ${nr} — Geschosse`}
                  inputMode="numeric"
                  value={entry.geschosse ?? ""}
                  onChange={(e) =>
                    update(entry.id, { geschosse: ziffernFilter(e.target.value, { maxLen: 2 }) })
                  }
                />
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Fläche (m²)</span>
                <input
                  className={styles.input}
                  name={`anbauten-${idx}-flaeche`}
                  aria-label={`Anbau ${nr} — Fläche (m²)`}
                  inputMode="decimal"
                  value={entry.flaeche ?? ""}
                  onChange={(e) =>
                    update(entry.id, { flaeche: ziffernFilter(e.target.value, { dezimal: true }) })
                  }
                />
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Einheiten</span>
                <input
                  className={styles.input}
                  name={`anbauten-${idx}-einheiten`}
                  aria-label={`Anbau ${nr} — Einheiten`}
                  inputMode="numeric"
                  value={entry.einheiten ?? ""}
                  onChange={(e) => update(entry.id, { einheiten: ziffernFilter(e.target.value) })}
                />
              </div>
            </div>
          </div>
        );
      })}
      <button type="button" className={styles.addBtn} onClick={add}>
        + Anbau hinzufügen
      </button>
    </div>
  );
};

export default AnbautenListe;
